import { useEffect, useState } from "react";
import { Route, Switch, useLocation } from "wouter";
import { UserProvider } from "../empreenderUI/UserContext";
import { EmpreenderApps } from "../files/AppsArray";
import { usePergunta } from "../contexts/PerguntaContext";
import { appPadrao, AppDaCentral, SLUG_PLUS } from "../lib/appsDaCentral";
import { origemDoSiteDeFora, PREFIXO_EMBUTIDO } from "../lib/rotas";
import { guardarOrigemLp, guardarWhatsAppDaPagina } from "../lib/whatsappDaPagina";
import TelaInicial from "./TelaInicial";
import TelaChat from "./TelaChat";
import i18n from "../i18n";

// Central dentro do iframe do botão "Ajuda" dos painéis (app.*.com.br). Entrada:
//   https://www.suporte.com.br/embed?app=<slug do app na SAK>&lang=pt|es
// Nas landing pages (bolha e FAQ embutido do faq.js) vem também &whatsapp=<número da página>: o
// "falar com atendente" vai para esse WhatsApp (lib/whatsappDaPagina.ts).
// Sem menu lateral nem barra do topo: o painel que embute já tem o cabeçalho com o X.
//
// Nome da pessoa (para "Olá, Yan!"): vem por postMessage, nunca pela URL. O painel manda
//   { tipo: "central:usuario", nome: "Yan Felipe" }
// depois de receber { tipo: "central:pronta" } desta página.

// O painel pode mandar o slug da SAK ("sak"), o slug em espanhol, o código do Plus ("SAK") ou o
// nome amigável ("sak"); qualquer um serve. Desconhecido: abre no Empreender Plus.
const encontrarApp = (valor: string | null): AppDaCentral => {
  const v = (valor || "").trim().toLowerCase();
  if (!v) return appPadrao;
  return (
    EmpreenderApps.find(
      (app) =>
        app.slug.toLowerCase() === v ||
        app.sluges.toLowerCase() === v ||
        app.eplus.toLowerCase() === v ||
        app.slugamigavel === v
    ) || appPadrao
  );
};

// Quem pode embutir: qualquer site (painéis, landing pages, a bolha do faq.js). Não há lista de
// liberados, que bloqueava sites nossos fora do padrão. Em troca, cada site é anotado (api/site.ts),
// site novo vira aviso no Slack e um admin bloqueia no painel (Integrações) o que não for nosso.
// A proteção de custo são os tetos de gravação e de gasto da OpenAI.
const idiomaValido = (valor: string | null) => (valor === "es" ? "es" : "pt");

const CentralEmbutida = () => {
  const [location, setLocation] = useLocation();
  const { setAppName, setAppSlug } = usePergunta();
  const [nome, setNome] = useState("");
  const [bloqueado, setBloqueado] = useState(false);

  // Site bloqueado no painel? Na dúvida (sem resposta), a central abre normalmente.
  useEffect(() => {
    const origem = origemDoSiteDeFora();
    if (!origem || origem === window.location.origin) return;
    fetch("/api/site", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ origem }) })
      .then((r) => (r.ok ? r.json() : null))
      .then((r) => setBloqueado(Boolean(r?.bloqueado)))
      .catch(() => {});
  }, []);

  // /embed?app=sak&lang=pt -> /embed/sak/pt (as telas da central navegam por caminho).
  useEffect(() => {
    // Lê o endereço real: o estado do roteador pode ainda não ter visto o redirecionamento
    // anterior e, sem o ?app=, a entrada cairia no Plus.
    const caminho = window.location.pathname.replace(/\/$/, "");
    if (caminho !== PREFIXO_EMBUTIDO) return;
    const params = new URLSearchParams(window.location.search);
    const app = encontrarApp(params.get("app"));
    const idioma = idiomaValido(params.get("lang"));
    guardarWhatsAppDaPagina(params.get("whatsapp"));
    guardarOrigemLp(params.get("lp"));
    if (i18n.language !== idioma) i18n.changeLanguage(idioma);
    setLocation(`${PREFIXO_EMBUTIDO}/${app.slugamigavel}/${idioma}`, { replace: true });
  }, [location, setLocation]);

  useEffect(() => {
    const aoReceber = (evento: MessageEvent) => {
      // Só o site que embute fala com a central (o nome serve só para a saudação).
      if (evento.source !== window.parent) return;
      if (evento.data?.tipo === "central:usuario" && typeof evento.data.nome === "string") {
        setNome(evento.data.nome.trim().split(/\s+/)[0].slice(0, 40));
      }
    };
    window.addEventListener("message", aoReceber);
    if (window.parent !== window) window.parent.postMessage({ tipo: "central:pronta" }, "*");
    return () => window.removeEventListener("message", aoReceber);
  }, []);

  const usarApp = (slugAmigavel: string, idioma: string) => {
    const app = EmpreenderApps.find((a) => a.slugamigavel === slugAmigavel) || appPadrao;
    if (i18n.language !== idioma) i18n.changeLanguage(idioma);
    setAppName(idioma === "es" ? app.namees : app.name);
    setAppSlug(idioma === "es" ? app.sluges : app.slug);
    return app;
  };

  if (bloqueado) {
    return (
      <div className="modo-embutido embutido-bloqueado">
        <p>{i18n.t("embutido_bloqueado")}</p>
        <a href="https://www.suporte.com.br" target="_blank" rel="noopener noreferrer">
          www.suporte.com.br
        </a>
      </div>
    );
  }

  return (
    // Sem appEndPoint o provider não tenta login (cookie de terceiro é bloqueado no iframe);
    // existe só porque partes da central leem o usuário da empreender-ui.
    <UserProvider hideApps iframe>
      <div className="modo-embutido">
        <Switch>
          <Route path={`${PREFIXO_EMBUTIDO}/:app/chat/:conversa/:lang`}>
            {(params) => {
              usarApp(params.app, idiomaValido(params.lang));
              return <TelaChat appSlugAmigavel={params.app} conversaAtual={params.conversa} />;
            }}
          </Route>
          <Route path={`${PREFIXO_EMBUTIDO}/:app/:lang`}>
            {(params) => {
              const app = usarApp(params.app, idiomaValido(params.lang));
              const idioma = idiomaValido(params.lang);
              return (
                <TelaInicial
                  appSlug={idioma === "es" ? app.sluges : app.slug}
                  appName={idioma === "es" ? app.namees : app.name}
                  saudacaoNome={nome}
                  // Só o painel do Plus (porta de entrada de todos os apps) troca de app; no
                  // painel da Sak, da Lily etc. a dúvida é sobre aquele app.
                  permitirTrocaApp={app.slugamigavel === SLUG_PLUS}
                />
              );
            }}
          </Route>
        </Switch>
      </div>
    </UserProvider>
  );
};

export default CentralEmbutida;
